import Vue from "vue";
import backend from "@/services/backend";
import * as RegulationPolicy from "@/services/regulation-policy";

// Stabilization commit 5: structured error returned by add_to_cart
// when the action would put the cart into a mixed-track state. UI
// surfaces (CheckoutAboutYou banner, cart UI) consume this code to
// render the "different regulatory tracks" affordance. The
// store is the first enforcement point; backend cart mutations and
// checkout keep fail-closed guards for stale or legacy mixed carts.
const ERR_MIXED_TRACK_CART = "CART_MIXED_TRACK_REJECTED";

const getDefaultState = () => {
  return {
    state: {
      cart: {
        items: [],
        active: false,
      },
      terms: {
        first: false,
        second: false,
        third: false,
        fourth: false,
        fifth: false,
      },
      payment_update_item: null,
      reservation: null,
      reservations: [],
      selected_account: null,
      mainStep: 1,
      mainStepReservation: 1,
      checkout_data: null,
      // checkout
      investor_entity: null,
      other_entity: null,
      investor: null,
      subscription_agreement_map: {},
      offering_id_map: {},
      selected_perk_ids: [],
      checkout_version: null,
      payment_method: "ach",
      investment_cancel_error: null,
      activity: [],
    },
  };
};

export default {
  namespaced: true,
  state: {
    cart: {
      items: [],
      active: false,
    },
    terms: {
      first: false,
      second: false,
      third: false,
      fourth: false,
      fifth: false,
    },
    reservation: null,
    selected_account: null,
    mainStep: 1,
    mainStepReservation: 1,
    checkout_data: null,
    // checkout
    investor_entity: null,
    other_entity: null,
    investor: null,
    subscription_agreement_map: {},
    offering_id_map: {},
    selected_perk_ids: [],
    checkout_version: null,
    payment_method: "ach",
    investment_cancel_error: null,
    activity: [],
  },

  actions: {
    resetCartState({ commit }) {
      commit("resetState");
    },
    ensure_active_cart(context) {
      if (!context.state.cart.active) {
        return context.dispatch("fetch_cart");
      }
      return Promise.resolve();
    },
    fetch_cart(context) {
      return backend.get_cart()
        .then((response) => {
          context.state.cart = response.data;
        })
        .catch((error) => {
          // In local/dev, unauthenticated users can hit this; don't hard-crash the UI.
          if (error?.response?.status === 401) {
            context.state.cart = { items: [], active: false };
            return;
          }
          throw error;
        });
    },
    async add_to_cart(context, item) {
      await context.dispatch("ensure_active_cart");
      // Stabilization commit 5: enforce single-track invariant at the
      // store boundary. Checkout assumes one track per cart; allowing
      // accumulation under different tracks created an implicit
      // assumption every consumer had to defend against.
      const incoming_track = RegulationPolicy.normalize(item.type);
      const existing_items = (context.state.cart && context.state.cart.items) || [];
      if (incoming_track && existing_items.length > 0) {
        const has_other_track = existing_items.some((existing) => {
          const t = RegulationPolicy.track_for_cart_item(existing);
          return t && t !== incoming_track;
        });
        if (has_other_track) {
          const err = new Error(
            "Cart already contains an investment under a different regulatory track. " +
            "Please remove the existing item before adding one under a different track."
          );
          err.code = ERR_MIXED_TRACK_CART;
          throw err;
        }
      }
      return backend
        .to_cart({
          home_id: item.home.id,
          invest_amount: item.invest_amount,
          type: item.type,
        })
        .then((response) => {
          context.state.cart.items.push(response.data);
        });
    },
    async remove_from_cart(context, item) {
      await context.dispatch("ensure_active_cart");
      return backend.remove_from_cart({ item: item.id }).then((response) => {
        context.state.cart.items = context.state.cart.items.filter(
          (x) => x.id !== item.id
        );
      });
    },
    submit_cart(context) {
      return backend
        .submit_cart({
          cart: context.state.cart.id,
          terms: context.state.terms,
          account: context.state.selected_account,
        })
        .then((response) => {
          context.state.cart.items = [];
          context.state.cart.active = false;
        });
    },
    set_reservation(context, item) {
      context.state.reservation = item;
    },
    set_reservations(context, item) {
      context.state.reservations = item;
    },
    set_checkout_items(context, items) {
      context.state.cart.items = items || [];
      context.state.cart.active = context.state.cart.items.length > 0;
      context.state.mainStep = 1;
      context.state.checkout_data = null;
      context.state.payment_method = "ach";
      return Promise.resolve();
    },
    set_transaction_update_item(context, item) {
      context.state.payment_update_item = item;
    },
    like_home(context, home) {
      return backend.like_home(home.id).then((response) => {
        context.commit("user/push_home_like", home.id, { root: true });
        home.likes += 1;
      });
    },
    unlike_home(context, home) {
      return backend.unlike_home(home.id).then((response) => {
        context.commit("user/remove_home_like", home.id, { root: true });
        home.likes -= 1;
      });
    },
    submit_set_payment_method(context, val) {
      context.commit("set_payment_method", val);
    },
    set_investment_error(context, val) {
      context.state.investment_cancel_error = val;
    },
    add_activity(context, val) {
      const entry = context.state.activity.find((item) => item.value === val);
      if (!entry) {
        context.state.activity.push({
          value: val,
          timestamp: new Date(),
        });
      } else {
        entry.timestamp = new Date();
      }
      console.log(context.state.activity);
    },
  },
  getters: {
    investment_cancel_error: (state) => {
      return state.investment_cancel_error;
    },
    get_payment_method: (state) => {
      return state.payment_method;
    },
  },
  mutations: {
    set_payment_method: function (state, val) {
      state.payment_method = val;
    },
    set_selected_account(state, val) {
      state.selected_account = val;
    },
    set_investor_entity(state, val) {
      state.investor_entity = val;
    },
    set_investor(state, val) {
      state.investor = val;
    },
    set_subscription_agreement_map(state, val) {
      state.subscription_agreement_map = val || {};
    },
    set_offering_id_map(state, val) {
      state.offering_id_map = val || {};
    },
    set_checkout_data(state, val) {
      state.checkout_data = val;
    },
    set_selected_perk_ids(state, val) {
      state.selected_perk_ids = Array.isArray(val) ? val : [];
    },
    set_checkout_version(state, val) {
      state.checkout_version = val || null;
    },
    set_main_step(state, val) {
      state.mainStep = val;
    },
    resetState(state) {
      Object.assign(state, getDefaultState().state);
    },
  },
};
